/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/control-has-associated-label */
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Checkbox } from "@/core/shared/v2/ui/checkbox";
import { Label } from "@/core/shared/v2/ui/label";
import type { AgreementStates } from "../../types";
import { getAgreementsStates } from "../../utils/get-agreement-states";
import { CheckboxField } from "../form-fields/checkbox-field";

export type CheckboxesFields = {
  acceptDataProcessing?: boolean;
  acceptNewsletter?: boolean;
  acceptMarketingPhoneContact?: boolean;
  acceptTouNPp?: boolean;
  acceptGroupAction?: boolean;
};

interface CheckboxesGroupProps {
  agreementStates?: AgreementStates;
  customAgreement?: string | null;
}

export const defaultAgreementStates: AgreementStates = {
  marketingPhoneContact: "hidden",
  newsletter: "not_required",
  acceptGroupAction: "hidden",
};

export const CheckboxesGroup = ({
  agreementStates = defaultAgreementStates,
  customAgreement,
}: CheckboxesGroupProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "GROUP_ACTIONS_ORDERING.CHECKBOXES" });
  const { register, errors, setValue, control } = useFormContext();

  const { acceptGroupActionHidden, newsletterHidden, marketingPhoneContactHidden } =
    getAgreementsStates(agreementStates);

  const isMarketingHidden = newsletterHidden && marketingPhoneContactHidden;
  const marketingConsentFieldName = marketingPhoneContactHidden ? "acceptNewsletter" : "acceptMarketingPhoneContact";

  const [isMarketingDetailsOpen, setIsMarketingDetailsOpen] = useState(false);

  const selectAllCheckboxes = (newValue: boolean) => {
    setValue("acceptDataProcessing", newValue, { shouldValidate: true });
    setValue("acceptTouNPp", newValue, { shouldValidate: true });
    // Skip channels whose checkbox is hidden — writing to them would record
    // consent the user never saw (config-level visibility = "channel disabled").
    if (!newsletterHidden) {
      setValue("acceptNewsletter", newValue, { shouldValidate: true });
    }
    if (!marketingPhoneContactHidden) {
      setValue("acceptMarketingPhoneContact", newValue, { shouldValidate: true });
    }
    if (!acceptGroupActionHidden) {
      setValue("acceptGroupAction", newValue, { shouldValidate: true });
    }
    if (customAgreement) {
      setValue("acceptCustomAgreement", newValue, { shouldValidate: true });
    }
  };

  // Inline expand/collapse toggle for the marketing consent. Rendered as a <span> (not a <button>)
  // because the browser coerces form controls to atomic inline-block, which always allows a line break
  // before them — a <span> stays truly inline so a non-breaking space can glue it to the surrounding text.
  const renderMarketingToggle = (className: string) => (
    // biome-ignore lint/a11y/useSemanticElements: must be inline; a <button> is forced to inline-block
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsMarketingDetailsOpen((v) => !v);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          setIsMarketingDetailsOpen((v) => !v);
        }
      }}
      className={`cursor-pointer text-dodger-blue-700 underline ${className}`}
    >
      {isMarketingDetailsOpen ? t("MARKETING_CONSENT_COLLAPSE") : t("MARKETING_CONSENT_EXPAND")}
    </span>
  );

  return (
    <>
      <div className="mb-4 w-fit">
        <div className="flex items-center gap-2">
          <Checkbox id="select-all" onCheckedChange={(checked) => selectAllCheckboxes(!!checked)} />
          <Label htmlFor="select-all" className="font-semibold text-base text-muted-foreground">
            {t("SELECT_ALL")}
          </Label>
        </div>
      </div>
      <div className="flex flex-col gap-3 pl-4">
        {/* Hidden field that mirrors the obligatory checkbox to keep RHF + yup happy. */}
        <input
          type="checkbox"
          name="acceptDataProcessing"
          ref={register}
          className="hidden"
          tabIndex={-1}
          aria-hidden
        />
        <Controller
          control={control}
          name="acceptTouNPp"
          defaultValue={false}
          render={({ onChange, value, ref }) => (
            <CheckboxField
              ref={ref}
              size="sm"
              labelClassName="text-muted-foreground text-[13px]"
              name="acceptTouNPp"
              checked={!!value}
              onCheckedChange={(checked) => {
                onChange(!!checked);
                setValue("acceptDataProcessing", !!checked, { shouldValidate: true });
              }}
              label={
                <Trans
                  i18nKey="GROUP_ACTIONS_ORDERING.CHECKBOXES.OBLIGATORY_CONSENT"
                  components={{
                    linkPolicies: (
                      // biome-ignore lint/a11y/useAnchorContent: content provided by Trans i18n component
                      <a
                        className="text-dodger-blue-700 underline"
                        href="/polityka-prywatnosci"
                        target="_blank"
                        rel="noopener"
                      />
                    ),
                    linkRules: (
                      // biome-ignore lint/a11y/useAnchorContent: content provided by Trans i18n component
                      <a className="text-dodger-blue-700 underline" href="/regulamin" target="_blank" rel="noopener" />
                    ),
                  }}
                />
              }
              error={errors.acceptDataProcessing?.message || errors.acceptTouNPp?.message}
            />
          )}
        />

        {!acceptGroupActionHidden && (
          <Controller
            control={control}
            name="acceptGroupAction"
            defaultValue={false}
            render={({ onChange, value, ref }) => (
              <CheckboxField
                ref={ref}
                size="sm"
                labelClassName="text-muted-foreground text-[13px]"
                name="acceptGroupAction"
                checked={!!value}
                onCheckedChange={(checked) => onChange(!!checked)}
                label={
                  <Trans
                    i18nKey="GROUP_ACTIONS_ORDERING.CHECKBOXES.ACTION_AGREEMENT"
                    components={{
                      promoRulesLink: (
                        // biome-ignore lint/a11y/useAnchorContent: content provided by Trans i18n component
                        <a
                          className="text-dodger-blue-700 underline"
                          href="/regulamin-promocji-darmowe-pobrania-krwi"
                          target="_blank"
                          rel="noopener"
                        />
                      ),
                      rodoLink: (
                        // biome-ignore lint/a11y/useAnchorContent: content provided by Trans i18n component
                        <a
                          className="text-dodger-blue-700 underline"
                          href="/informacja-rodo-darmowe-pobrania-krwi"
                          target="_blank"
                          rel="noopener"
                        />
                      ),
                    }}
                  />
                }
                error={errors.acceptGroupAction?.message}
              />
            )}
          />
        )}
        {!isMarketingHidden && (
          <div>
            {!marketingPhoneContactHidden && (
              <input
                type="checkbox"
                name="acceptNewsletter"
                ref={register}
                className="hidden"
                tabIndex={-1}
                aria-hidden
              />
            )}
            <Controller
              control={control}
              name={marketingConsentFieldName}
              defaultValue={false}
              render={({ onChange, value, ref }) => (
                <CheckboxField
                  ref={ref}
                  size="sm"
                  labelClassName="text-muted-foreground text-[13px]"
                  name={marketingConsentFieldName}
                  checked={!!value}
                  onCheckedChange={(checked) => {
                    onChange(!!checked);
                    if (!newsletterHidden && !marketingPhoneContactHidden) {
                      setValue("acceptNewsletter", !!checked, { shouldValidate: true });
                    }
                  }}
                  label={
                    <>
                      {t("MARKETING_CONSENT")}
                      {/* Collapsed: the toggle sits inline at the END of the consent text, glued by a non-breaking
                          space so it never orphans onto its own line nor jams against the right margin. When
                          expanded it moves to the end of the details below (see further down), so it isn't left
                          stranded up in the consent line. */}
                      {!isMarketingDetailsOpen && (
                        <>
                          {"\u00A0"}
                          {renderMarketingToggle("")}
                        </>
                      )}
                    </>
                  }
                  error={errors.acceptNewsletter?.message || errors.acceptMarketingPhoneContact?.message}
                />
              )}
            />
            {isMarketingDetailsOpen && (
              <div className="mt-1 ml-7 text-muted-foreground text-sm">
                <Trans
                  i18nKey="GROUP_ACTIONS_ORDERING.CHECKBOXES.MARKETING_CONSENT_DETAILS"
                  components={{
                    paragraph: <p className="mb-2 last:mb-0" />,
                    bold: <strong className="font-semibold" />,
                  }}
                />
                {/* Expanded: the collapse toggle sits at the END of the revealed text, on its own line just
                    below the last paragraph. */}
                {renderMarketingToggle("inline-block")}
              </div>
            )}
          </div>
        )}
        {customAgreement && (
          <Controller
            control={control}
            name="acceptCustomAgreement"
            defaultValue={false}
            render={({ onChange, value, ref }) => (
              <CheckboxField
                ref={ref}
                size="sm"
                labelClassName="text-muted-foreground text-[13px]"
                name="acceptCustomAgreement"
                checked={!!value}
                onCheckedChange={(checked) => onChange(!!checked)}
                label={
                  <div
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: `${customAgreement}*`,
                    }}
                    className="prose"
                  />
                }
                error={errors.acceptCustomAgreement?.message}
              />
            )}
          />
        )}
      </div>
    </>
  );
};
